/**
 * SAB-927: may this user start the draft, and if not, what stops them?
 *
 * ── WHY THIS IS A PURE FUNCTION AND NOT A FEW && IN THE JSX ─────────────────
 *
 * The control this feeds did not exist for three days. `nfl-initialize-draft` had exactly
 * one caller in the whole client -- NFLDraftPage.tsx:1152 -- and it sat inside a branch
 * gated on `draft_status === 'pending'`, in a room that rule (e) only ever serves for
 * `in_progress` and `paused`. The button was unreachable by construction from the routing
 * cutover (dce0457, 2026-08-27 21:06 UTC) onward; the last draft ever started was five
 * hours earlier, and fourteen leagues piled up at pending behind it.
 *
 * So the replacement's gating is the part worth testing on its own. A start control with
 * WEAKER gating than the one it restores would be worse than no control at all: it would
 * offer a real-money-adjacent, effectively irreversible action (a draft, once initialized,
 * is cancel-and-rebuild, not undo) in states where it must not be offered.
 *
 * ── THE CONDITIONS, AND WHERE EACH ONE COMES FROM ───────────────────────────
 *
 * Ordered to match `supabase/functions/nfl-initialize-draft/index.ts`, so the reason shown
 * here is the SAME one the server would return first. A client that blocks in a different
 * order than the server tells the user to fix B while the server is refusing on A.
 *
 *   server :81   COMMISSIONER_UNPAID -> `unpaid`
 *   server :94   teams.length < 2    -> `tooFewTeams`
 *   server :102  entry_fee > 0 && teams.length < max_teams -> `notFull`
 *
 * ── THE ONE CONDITION THE LEGACY BUTTON DID NOT HAVE ────────────────────────
 *
 * `notFull`. The legacy client checked only `teams.length < 2`, so on a PAID league at
 * 5/10 it rendered an enabled button and the server refused with
 * "League must be full to draft -- 5/10 joined". That mismatch predates the cutover.
 *
 * It is added here deliberately, and it is safe in the only direction that matters: it
 * never ENABLES anything the legacy button enabled. It disables an action the server was
 * already certain to reject, and replaces a red toast with a stated reason. The gate it
 * mirrors is the A-1 prize-pool certificate -- a paid league's pool is entry_fee x
 * max_teams, so drafting one short would lock a pool that can never be collected.
 *
 * ── DELIBERATELY MATCHING LEGACY: the fee check while it is still loading ───
 *
 * `feeLoading` does NOT block. That is `commissionerUnpaid = required && !loading && !paid`
 * from NFLDraftPage.tsx:182, kept verbatim: the point of the !loading term is that a
 * commissioner who HAS paid must never see "unpaid" flash at them before the check
 * resolves. Blocking during the window would be stricter, but it would also be a second
 * deviation from the button being restored, and the server refuses correctly either way.
 */

export interface StartDraftGateInput {
  /** The league's draft_status. Only pending (or unset) can be started. */
  draftStatus: string | null | undefined;
  isCommissioner: boolean;
  /** Teams currently in the league. */
  teamCount: number;
  /** The league's roster of slots. Null when unknown -- see notFull below. */
  maxTeams: number | null | undefined;
  /** The league's entry fee. > 0 makes it a PAID league. */
  entryFee: number;
  /** useCommissionerFeeStatus: is a commissioner fee owed on this league at all? */
  feeRequired: boolean;
  /** useCommissionerFeeStatus: is the paid check still in flight? */
  feeLoading: boolean;
  /** useCommissionerFeeStatus: has it been paid? */
  feePaid: boolean;
}

export interface StartDraftGate {
  /**
   * Whether a draft can be started here AT ALL -- i.e. the league is pending. Drives the
   * empty-state copy for everyone, commissioner or not.
   */
  startable: boolean;
  /** Whether to render the control. Commissioner-only, as the legacy button was. */
  showControl: boolean;
  /** Null when the control is enabled; otherwise why it is not. */
  reason: string | null;
}

/** The two states in which a draft has not yet been initialized. */
export function isPendingDraft(draftStatus: string | null | undefined): boolean {
  return draftStatus === 'pending' || draftStatus == null;
}

export function resolveStartDraftGate(input: StartDraftGateInput): StartDraftGate {
  const startable = isPendingDraft(input.draftStatus);
  const showControl = startable && input.isCommissioner;

  if (!showControl) return { startable, showControl, reason: null };

  // 1. Server :81 -- a paid league's commissioner must have paid their own entry fee.
  const unpaid = input.feeRequired && !input.feeLoading && !input.feePaid;
  if (unpaid) {
    return {
      startable,
      showControl,
      reason: `Pay your $${input.entryFee} entry fee first — the draft unlocks once it is in.`,
    };
  }

  // 2. Server :94 -- a draft needs somebody to draft against.
  if (input.teamCount < 2) {
    return {
      startable,
      showControl,
      reason: `Need at least 2 teams to draft — ${input.teamCount} joined.`,
    };
  }

  // 3. Server :102 -- a PAID league must be full, or its prize pool cannot be collected.
  //    Skipped when max_teams is unknown rather than guessed: inventing a cap here would
  //    block a startable league, and the server still holds the real gate.
  const isPaid = input.entryFee > 0;
  if (isPaid && input.maxTeams != null && input.teamCount < input.maxTeams) {
    return {
      startable,
      showControl,
      reason: `A paid league must be full to draft — ${input.teamCount}/${input.maxTeams} joined.`,
    };
  }

  return { startable, showControl, reason: null };
}
